'use client';

import React from 'react';
import { Typography } from 'src/components';
import {
  faExternalLink,
  faTrashCanXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { sentenceCase } from 'src/utils/sentenceCase/sentenceCase';
import { CRMObject } from './types';
import { useAuth } from 'src/context/auth';
import { canUnlinkCRMRecord } from 'src/components/PermissionGuard/checks';

interface CRMObjectDetailsProps {
  crmObject: CRMObject;
  onDelete?: (crmLink: CRMObject) => void;
}

const CRMObjectDetails: React.FC<CRMObjectDetailsProps> = ({
  crmObject,
  onDelete,
}) => {
  const isThirdParty = crmObject.crm?.toLowerCase() === 'third_party';
  const crmDisplayName = isThirdParty ? 'CRM' : sentenceCase(crmObject.crm);
  const { user } = useAuth();
  const canUnlink = canUnlinkCRMRecord(user);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete) {
      onDelete(crmObject);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-1 flex-col">
        <Typography className="text-tertiary text-sm">
          {sentenceCase(crmObject.crm_object_type)}
        </Typography>
        <div className="flex items-center gap-2">
          <a
            href={crmObject.crm_link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sky-700 hover:text-sky-800 cursor-pointer text-sm font-semibold hover:underline flex flex-row items-center"
          >
            View in {crmDisplayName || 'CRM'}
            <Icon
              className="pl-1"
              height={12}
              width={12}
              icon={faExternalLink}
            />
          </a>
          {canUnlink && onDelete && (
            <button
              onClick={handleDeleteClick}
              className="text-neutral-500 hover:text-red-600 transition-colors"
              aria-label="Delete CRM link"
              data-testid={`delete-crm-link-${crmObject.id}`}
            >
              <Icon icon={faTrashCanXmark} className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <div className="flex gap-1 flex-col">
        <Typography className="text-tertiary text-sm w-28">Status</Typography>
        <Typography className="text-primary font-semibold text-sm">
          {sentenceCase(crmObject.crm_status) || 'No Status'}
        </Typography>
      </div>
      {!isThirdParty && (
        <div className="flex gap-1 flex-col">
          <Typography className="text-tertiary text-sm">Owner</Typography>
          <Typography className="text-primary font-semibold text-sm">
            {`${crmObject.owner?.firstname || ''} ${
              crmObject.owner?.lastname || ''
            }`}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default CRMObjectDetails;
